import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { TextArea } from '../ui/TextArea';
import { ContactRequest, ContactResponse } from '../../types';
import { publicApi } from '../../services/publicApi';
import toast from 'react-hot-toast';

const contactFormSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters'),
  email: z.string()
    .email('Please enter a valid email address')
    .max(200, 'Email must be less than 200 characters'),
  subject: z.string()
    .min(5, 'Subject must be at least 5 characters')
    .max(100, 'Subject must be less than 100 characters'),
  message: z.string()
    .min(10, 'Please provide more details (at least 10 characters)')
    .max(2000, 'Message must be less than 2000 characters'),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

interface PublicContactFormProps {
  onSuccess?: (response: ContactResponse) => void;
  onCancel?: () => void;
}

export default function PublicContactForm({ onSuccess, onCancel }: PublicContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onChange',
  });

  const messageLength = watch('message')?.length || 0;

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    try {
      const requestData: ContactRequest = {
        name: data.name.trim(),
        email: data.email.trim(),
        subject: data.subject.trim(),
        message: data.message.trim(),
      };

      const response = await publicApi.submitContact(requestData);

      if (response.success) {
        toast.success(response.message);
        reset();
        onSuccess?.(response);
      } else {
        toast.error(response.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Contact Support
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Have a question or need help? Send us a message and we'll get back to you as soon as possible.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Name */}
        <div>
          <Input
            label="Name"
            {...register('name')}
            placeholder="Your name"
            error={errors.name?.message}
            leftIcon={<AlertCircle className="w-4 h-4" />}
            disabled={isSubmitting}
          />
        </div>

        {/* Email */}
        <div>
          <Input
            label="Email"
            type="email"
            {...register('email')}
            placeholder="your@email.com"
            error={errors.email?.message}
            leftIcon={<AlertCircle className="w-4 h-4" />}
            disabled={isSubmitting}
          />
        </div>

        {/* Subject */}
        <div>
          <Input
            label="Subject"
            {...register('subject')}
            placeholder="Brief description of your inquiry"
            error={errors.subject?.message}
            disabled={isSubmitting}
          />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Message
          </label>
          <TextArea
            id="message"
            {...register('message')}
            rows={5}
            placeholder="Please provide as much detail as possible about your question or issue."
            className="w-full"
            disabled={isSubmitting}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.message ? (
              <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.message.message}
              </p>
            ) : (
              <div />
            )}
            <span className={`text-sm ${
              messageLength > 1800
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {messageLength}/2000
            </span>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            disabled={!isValid || isSubmitting}
            leftIcon={isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
        </div>
      </form>
    </div>
  );
}
